import React, {
    useState,
    useEffect,
    useMemo,
} from 'react';

import { getBasketData, refetchBasketData } from "../queries/global"
import BasketSummary from '../components/basket-summary';
import LoadingIndicator from './loading-indicator';
import ChevronIcon from '../svg/chevron'

import * as UTILS from "../utils/utils";
import * as QUERIES from "../queries/query-functions";
import config from "../config/config";
import { DonationSummaryItem, GiftVoucherItem, MembershipSubscriptionsItem, SeatSelectionSummaryItem, SupplementarySummaryItem } from '../book-online/seat-selection-summary';
import MembershipPrompt from '../basket/membership-prompt';
import { useQueries } from '@tanstack/react-query';

const is_disclosure = window.matchMedia("(max-width: 48em)").matches;

export default function OrderSummary({setCheckedForPriority, checkedForPriority}) {

    const [disclosureOpen, setDisclosureOpen] = useState(false);
    const [groupedTickets, setGroupedTickets] = useState(null);
    const [groupedInstances, setGroupedInstances] = useState(null);
    const [total, setTotal] = useState(null);

    const { data: basketData, isFetching } = getBasketData();

    useEffect(() => {
        if (!basketData || !basketData.tickets) return;

        let tickets = basketData.tickets;
        let tickets_total = basketData.total;

        const grouped_tickets = UTILS.groupTicketsByInstance(tickets);

        const grouped_instances = grouped_tickets.reduce((acc, instance) => {
            const day = instance.instance_date_utc.split("T")[0];
            
            if(!acc[day]) {
                acc[day] = []
            }

            acc[day].push(instance);

            return acc;
        }, {})

        setGroupedTickets(grouped_tickets);
        setGroupedInstances(grouped_instances);
        setTotal(tickets_total.toFixed(2))

    }, [basketData]);

      //catch the message from the iframe and if it's the message we expect, then we know the user has logged in and we can refresh the order summary
    useEffect(() => {
        function handleMessage(event) {
            console.log("message received", event.data)
            if (event.data && event.data["refresh basket"] === true){
                refetchBasketData();
                console.log("message received, refreshing basket")
            }
        }
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const planIds = useMemo(() => {
        if (!basketData?.tickets) return [];
    
        return [
            ...new Set(basketData.tickets.map(ticket => ticket.planId)),
        ];
    }, [basketData]);

    const planQueries = useQueries({
        queries: planIds.map(id => ({
            queryKey: ["planData", id],
            queryFn: () => QUERIES.getPlanData(id),
            staleTime: 86400000,
        })),
    });

    const plansMap = useMemo(() => {
        const map = {};

        planQueries.forEach(q => {
            if (q.data) {                
                map[q.data.plan_id] = q.data;
            }
        });

        return map;
    }, [planQueries]);

    return (
        <>
        {is_disclosure &&
            <button type="button" 
                    className='btn order-summary__disclosure-button' 
                    onClick={() => setDisclosureOpen(!disclosureOpen)} 
                    aria-expanded={disclosureOpen} 
                    aria-controls="orderSummary">
                        Order summary
                        <ChevronIcon />
            </button>
        }
        <div id="orderSummary" className="order-summary" {...(is_disclosure ? {hidden: !disclosureOpen, "aria-hidden": !disclosureOpen} : {})}>
            {groupedTickets?.length || basketData?.giftVouchers?.length || basketData?.donations?.length || basketData?.membershipSubscriptions?.length ? 
                <>
                    <h2 className="booking-pathway__header">Order summary</h2>
                    <div className='order-summary__list'>
                        {groupedInstances && 
                            Object.values(groupedInstances).map((groupedInstance, index) => 
                                <SummaryCheckoutItem plansMap={plansMap} groupedInstance = {groupedInstance} />)
                        }

                        {!!basketData.donations && 
                            basketData.donations.map((donation) =>
                                <div key={donation.id} className='basket-summary__item'>
                                    <DonationSummaryItem donationsTotal={donation.amount} donationId={donation.id} />
                                </div>
                            )
                        }
                        {!!basketData.membershipSubscriptions && 
                            basketData.membershipSubscriptions.map((membership) =>
                                <div key={membership.id} className='basket-summary__item'>
                                    <MembershipSubscriptionsItem membership={membership} setCheckedForPriority={setCheckedForPriority} checkedForPriority={checkedForPriority} />
                                </div>
                            )
                        } 

                        {
                            !!basketData.giftVouchers && 
                                basketData.giftVouchers.map((giftVoucher) => 
                                    <div key={giftVoucher.id} className='basket-summary__item basket-summary__item--gift-voucher'>
                                        <GiftVoucherItem giftVoucher={giftVoucher} />
                                    </div>
                                )
                        }
                    </div>
                    <MembershipPrompt isSummary justSummaryText/>
                    <BasketSummary />
                </>
            : null}

            {is_disclosure &&
                <button type="button" 
                        className='btn order-summary__disclosure-button--hide' 
                        onClick={() => setDisclosureOpen(!disclosureOpen)} 
                        aria-expanded={disclosureOpen} 
                        aria-controls="orderSummary">
                            Hide summary
                </button>
            }
        </div>
        </>
    );
}

const SummaryCheckoutItem = ({plansMap, groupedInstance}) => {
    const supplementaryProtectionTicketId = groupedInstance.find((instance) => instance.event_name.toLowerCase().includes("refund"))?.instance_id;
    const instatanceName = groupedInstance.find(instance => instance.tickets?.[0]?.seat || (plansMap[instance.tickets[0]?.planId]?.type === 'Unreserved' && instance.tickets[0].event.attribute_FirstTierArtform !== 'None'))?.event_name;

    return (
        <div className='basket-summary'>
            <h2 className='booking-pathway__header--aside'>{instatanceName}</h2>
            <ul role="list" className="basket-summary__items basket-summary__items--seats">
                {groupedInstance.map((instance) => 
                    (instance.tickets?.[0]?.seat || (plansMap[instance.tickets[0]?.planId]?.type === 'Unreserved' && instance.tickets[0].event.attribute_FirstTierArtform !== 'None')) ? 
                        instance.seats.map((ticket) => (
                            <li key={ticket.ticket.id} className='basket-summary__item'>
                                <SeatSelectionSummaryItem ticket={ticket} supplementaryProtectionTicketId = {supplementaryProtectionTicketId} />
                            </li>
                        ))
                    : <li key={instance.event_id} className='basket-summary__item'>
                        <SupplementarySummaryItem supplementaryGroup = {instance} />
                    </li>                
                )}                              
            </ul>
        </div>
    )
}